/**
 * @fileoverview App-layer shell composing the shared dashboard layout with push notifications and
 * the RBAC gate.
 * @description Wraps DashboardLayout (shared/components) with useFcmRegistration
 * (features/notifications) so the dashboard-area routes register for push notifications once
 * signed in, and with useCurrentSession (features/auth) so GET /identity/me resolves — and its
 * privileges become the nav/route source of truth — before any dashboard-area page renders. This
 * composition has to live in app/ rather than directly inside DashboardLayout itself, since shared
 * may not import a feature (ADR-0002's one-directional layering): DashboardLayout and NavMenu take
 * the resolved privileges/notifications as plain props instead. A pending /me blocks rendering with
 * a centered loading state; a failed first load reuses the shared error-state card, since this genuinely
 * is a "the app can't show you your dashboard" failure. A failed background re-fetch (tab focus,
 * navigation, a `403 / 2008` — see useKeepAccessCurrent) is not: the last known privileges stay on
 * screen, so a blip never blanks a working app. Because those re-fetches replace the privileges
 * passed down, the menu, routes and actions re-render with the corrected access.
 *
 * The notification bell's dropdown (DashboardHeaderNotifications) is entirely presentational —
 * its data and mark-read mutations are resolved by useDashboardNotifications (this folder) and
 * passed down as one `notifications` prop, same boundary reason as `bank` below.
 *
 * A privilege set made entirely of `banking:*` keys (this app's one-module-per-role design — see
 * the 9-role RBAC plan) also triggers a `useBankingData` fetch here, so the signed-in user's own
 * bank name/tagline can be resolved once and passed down as `bank`, same boundary reason as
 * notifications above. It's intentionally non-blocking — if that fetch is still pending, `bank` is
 * simply left `undefined` for this render and DashboardLayout/DashboardHeader fall back to their
 * default InsightBox branding for a moment, rather than adding a second loading gate.
 *
 * The account menu's Organisation Details item opens the same dialog My Page does. The item is
 * offered to anyone who can use My Page (the roles that may read the details), and the dialog is
 * editable only with the organisation-update privilege, which only the Super Admin holds. The
 * dialog is mounted only while open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import serverErrorIcon from '@/assets/svg/errors/icon-server-error.svg'
import { ProfileModal, useCurrentSession, useKeepAccessCurrent } from '@/features/auth'
import { useBankingData } from '@/features/banking'
import { NotificationPreferencesPanel, useFcmRegistration } from '@/features/notifications'
import { OrganisationDetailsModal } from '@/features/sales'
import AuthLayout from '@/shared/components/layouts/AuthLayout'
import DashboardLayout from '@/shared/components/layouts/DashboardLayout'
import ErrorStateCard from '@/shared/components/molecules/ErrorStateCard'
import {
  ORGANISATION_UPDATE_PRIVILEGE,
  ORGANISATION_VIEW_PRIVILEGE,
} from '@/shared/constants/organisation-privileges.constants'

import { useDashboardNotifications } from './useDashboardNotifications'

/**
 * @component
 */
function DashboardShell() {
  const { t } = useTranslation()
  useFcmRegistration()
  useKeepAccessCurrent()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isOrganisationOpen, setIsOrganisationOpen] = useState(false)

  const currentSessionQuery = useCurrentSession()
  const notifications = useDashboardNotifications()
  const privileges = currentSessionQuery.data?.privileges ?? []
  const canViewOrganisation = privileges.includes(ORGANISATION_VIEW_PRIVILEGE)

  /**
   * @description Opens the profile dialog.
   */
  const openProfile = () => setIsProfileOpen(true)

  /**
   * @description Closes the profile dialog.
   */
  const closeProfile = () => setIsProfileOpen(false)

  /**
   * @description Opens the organisation details dialog.
   */
  const openOrganisation = () => setIsOrganisationOpen(true)

  /**
   * @description Closes the organisation details dialog.
   */
  const closeOrganisation = () => setIsOrganisationOpen(false)

  const isBanking = privileges.some((privilege) => privilege.startsWith('banking:'))
  const bankingDataQuery = useBankingData(isBanking)

  if (currentSessionQuery.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-input-bg">
        <span
          className="h-9 w-9 animate-spin rounded-full border-[3px] border-primary/20 border-t-primary"
          role="status"
          aria-label={t('layout.loading')}
        />
      </div>
    )
  }

  if (currentSessionQuery.isLoadingError) {
    return (
      <AuthLayout>
        <ErrorStateCard
          statusCode="500"
          icon={serverErrorIcon}
          accent="error"
          title={t('errors.serverError.title')}
          description={t('errors.serverError.description')}
          showRetry
        />
      </AuthLayout>
    )
  }

  const bank =
    isBanking && bankingDataQuery.data
      ? { name: bankingDataQuery.data.meta.bankName, tagline: bankingDataQuery.data.meta.tagline }
      : undefined

  return (
    <>
      <DashboardLayout
        privileges={privileges}
        notifications={notifications}
        onOpenProfile={openProfile}
        {...(canViewOrganisation && { onOpenOrganisation: openOrganisation })}
        {...(bank && { bank })}
      />
      <ProfileModal
        open={isProfileOpen}
        onClose={closeProfile}
        notificationsTab={<NotificationPreferencesPanel />}
      />
      {isOrganisationOpen && (
        <OrganisationDetailsModal
          canEdit={privileges.includes(ORGANISATION_UPDATE_PRIVILEGE)}
          onClose={closeOrganisation}
        />
      )}
    </>
  )
}

export default DashboardShell
